import * as Sentry from '@sentry/react';
import axios, { AxiosError } from 'axios';
import { getApiErrorCode } from './error';

// 'tatra' is the standalone Tatra Kopřivnice deployment (GCP project
// s4e-tatra). It reports into the same Sentry project as safe4entry, so it
// needs its own environment to be tellable apart - both are production builds.
type Env = 'dev' | 'local' | 'stage' | 'staging' | 'production' | 'tatra';

export function initSentry(dnsUrl: string, environment: Env) {
  let tracesSampleRate = 0;
  let replaysSessionSampleRate = 0;

  if (
    environment === 'production' ||
    environment === 'tatra' ||
    environment === 'stage' ||
    environment === 'staging'
  ) {
    tracesSampleRate = 0.1; // Sample 10% in production/stage/staging
    replaysSessionSampleRate = 0.1; // Sample 10% in production/stage/staging
  }

  Sentry.init({
    dsn: dnsUrl,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.replayCanvasIntegration(),
    ],
    tracesSampleRate,
    replaysSessionSampleRate, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: environment,
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (!axios.isAxiosError(error)) return event;

      // Connectivity failures (no HTTP response at all) are expected on flaky networks and
      // BaseService already shows the user an error toast, so reporting them - typically as
      // unhandled rejections - is just noise.
      if (error.code === AxiosError.ERR_NETWORK) return null;

      // Same for a response carrying a business error code: an expected, translated message
      // BaseService has already put in front of the user. Callers deliberately let the rejection
      // propagate (it is what stops the success path from running), so it often surfaces as an
      // unhandled rejection - not a defect. Anything without a code (5xx, unexpected payloads)
      // still gets reported.
      if (getApiErrorCode(error)) return null;

      return event;
    },
  });
}
